import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import TurmaModal from '../components/modals/TurmaModal';
import SearchInput from '../components/SearchInput';
import { normalizeSearch } from '../utils/formatters';
import { Pencil, Trash2 } from 'lucide-react';
import type { Turma } from '../types';

interface SortRule {
  column: string;
  dir: 'asc' | 'desc';
}

const Turmas: React.FC = () => {
  const navigate = useNavigate();
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [professores, setProfessores] = useState<{ id: string; nome: string }[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Turma | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [resTurmas, resProf, resAlunos] = await Promise.all([
        api.get('/turmas'),
        api.get('/professores'),
        api.get('/alunos?ativo=true'),
      ]);
      const alunosPorTurma: Record<string, number> = {};
      for (const a of resAlunos.data) {
        if (a.turma_id) alunosPorTurma[a.turma_id] = (alunosPorTurma[a.turma_id] || 0) + 1;
      }
      setTurmas(resTurmas.data.map((t: any) => ({
        ...t,
        alunos_count: alunosPorTurma[t.grupo_id] || 0,
      })));
      setProfessores(resProf.data);
    } catch (err) {
      console.error('Erro ao carregar turmas', err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const handler = () => { if (!document.hidden) carregar(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [carregar]);

  const handleSave = async (data: any) => {
    try {
      if (editando) {
        await api.put(`/turmas/${editando.id}`, data);
      } else {
        await api.post('/turmas', data);
      }
      setModalOpen(false);
      setEditando(null);
      await carregar();
    } catch (err: any) {
      console.error('Erro ao salvar turma', err);
      const msg = err.response?.data?.error || 'Erro ao salvar turma';
      alert(msg);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remover esta turma?')) return;
    try {
      await api.delete(`/turmas/${id}`);
      await carregar();
    } catch (err) {
      console.error('Erro ao remover turma', err);
    }
  };

  const professorNome = (professorId?: string) =>
    professores.find((p) => p.id === professorId)?.nome || '-';

  const [sortRules, setSortRules] = useState<SortRule[]>([]);

  const toggleSort = (column: string) => {
    setSortRules((prev) => {
      const idx = prev.findIndex((r) => r.column === column);
      if (idx === 0) {
        if (prev[0].dir === 'asc') return [{ column, dir: 'desc' }, ...prev.slice(1)];
        return prev.slice(1);
      }
      return [{ column, dir: 'asc' }, ...prev.filter((r) => r.column !== column)];
    });
  };

  const sortIcon = (column: string) => {
    const idx = sortRules.findIndex((r) => r.column === column);
    if (idx === -1) return null;
    const dir = sortRules[idx].dir;
    return (
      <span className="ml-1 text-xs text-primary-600 dark:text-primary-400">
        {idx > 0 && <sup className="text-[10px]">{idx + 1}</sup>}
        {dir === 'asc' ? '\u25B2' : '\u25BC'}
      </span>
    );
  };

  const thSort = (column: string, label: string) => (
    <button
      type="button"
      onClick={() => toggleSort(column)}
      className="font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 text-left text-sm whitespace-nowrap"
    >
      {label}
      {sortIcon(column)}
    </button>
  );

  const filtered = useMemo(() => {
    let data = turmas.filter((t) => {
    if (!filtro) return true;
    const q = normalizeSearch(filtro);
    return (
      normalizeSearch(t.label).includes(q) ||
      normalizeSearch(t.grupo_id || '').includes(q) ||
      normalizeSearch(t.horario || '').includes(q) ||
      normalizeSearch(t.nivel || '').includes(q) ||
      normalizeSearch(professorNome(t.professor_id)).includes(q)
    );
  });

    for (let i = sortRules.length - 1; i >= 0; i--) {
      const { column, dir } = sortRules[i];
      data.sort((a, b) => {
        let va: any, vb: any;
        switch (column) {
          case 'label': va = a.label.toLowerCase(); vb = b.label.toLowerCase(); break;
          case 'horario': va = a.horario || ''; vb = b.horario || ''; break;
          case 'nivel': va = a.nivel || ''; vb = b.nivel || ''; break;
          case 'professor': va = professorNome(a.professor_id); vb = professorNome(b.professor_id); break;
          case 'faixa_etaria': va = a.faixa_etaria || ''; vb = b.faixa_etaria || ''; break;
          case 'lotacao': va = a.alunos_count ?? 0; vb = b.alunos_count ?? 0; break;
          default: return 0;
        }
        if (va < vb) return dir === 'asc' ? -1 : 1;
        if (va > vb) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [turmas, filtro, sortRules]);

  const lotacaoClass = (count: number, cap?: number) => {
    if (!cap) return '';
    if (count > cap) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    if (count === cap) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
    return '';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Turmas</h1>
        <button
          onClick={() => { setEditando(null); setModalOpen(true); }}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          + Nova Turma
        </button>
      </div>

      <SearchInput
        value={filtro}
        onChange={setFiltro}
        placeholder="Buscar por turma, grupo ID, horário, nível ou professor..."
        className="w-full max-w-md"
      />

      {carregando ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Carregando...</p>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="text-left px-4 py-2">{thSort('label', 'Turma')}</th>
                <th className="text-left px-4 py-2">{thSort('horario', 'Horário')}</th>
                <th className="text-left px-4 py-2">{thSort('nivel', 'Nível')}</th>
                <th className="text-left px-4 py-2">{thSort('professor', 'Professor')}</th>
                <th className="text-left px-4 py-2">{thSort('faixa_etaria', 'Faixa Etária')}</th>
                <th className="text-left px-4 py-2">{thSort('lotacao', 'Lotação')}</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500 dark:text-gray-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map((t) => {
                const alunosCount = t.alunos_count ?? 0;
                return (
                  <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td
                      className="px-4 py-2 font-medium text-gray-800 dark:text-gray-100"
                      title={t.grupo_id || t.label}
                    >
                      {t.label}
                    </td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{(t.horario || '').slice(0, 5)}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{t.nivel || '-'}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{professorNome(t.professor_id)}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{t.faixa_etaria || '-'}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${lotacaoClass(alunosCount, t.capacidade)}`}
                      >
                        {alunosCount}/{t.capacidade || '∞'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setEditando(t); setModalOpen(true); }}
                        className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 ml-1 align-middle"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ml-1 align-middle"
                        title="Remover"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                    Nenhuma turma encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TurmaModal
        open={modalOpen}
        turma={editando}
        professores={professores}
        onSave={handleSave}
        onNavigateToAlunos={() => navigate('/alunos')}
        onClose={() => { setModalOpen(false); setEditando(null); }}
      />
    </div>
  );
};

export default Turmas;
